<script lang="ts">
  import type { InningsResult } from '@ipl-sim/engine-cricket';
  import { figures, oversFrom } from '../lib/commentary';

  interface Props {
    innings: readonly { name: string; result: InningsResult }[];
    onback: () => void;
  }
  let { innings, onback }: Props = $props();

  /**
   * Extras are what the total has that the batters do not. Deriving it rather
   * than tracking it separately means the scorecard can never fail to add up:
   * if the two ever disagreed, this is the number that would be wrong.
   */
  function extras(r: InningsResult): { total: number; w: number; nb: number; b: number; lb: number } {
    const off = r.batting.reduce((n, b) => n + b.runs, 0);
    const counts = { w: 0, nb: 0, b: 0, lb: 0 };
    for (const e of r.events) {
      if (!e.extra) continue;
      if (e.extra.kind === 'wide') counts.w += e.extra.runs;
      else if (e.extra.kind === 'noball') counts.nb += e.extra.runs;
      else if (e.extra.kind === 'bye') counts.b += e.extra.runs;
      else counts.lb += e.extra.runs;
    }
    return { total: r.runs - off, ...counts };
  }

  function how(b: InningsResult['batting'][number], r: InningsResult): string {
    if (!b.out) return b.balls > 0 ? 'not out' : 'did not bat';
    const d = b.dismissal;
    if (!d) return 'out';
    const name = (id?: string) =>
      r.bowling.find((x) => x.player.id === id)?.player.name ??
      r.batting.find((x) => x.player.id === id)?.player.name ??
      '';
    // Scorecard conventions, not the engine's vocabulary: a bowled dismissal
    // is written "b Bowler", never "bowled b Bowler".
    if (d.kind === 'run out') return 'run out';
    if (d.kind === 'caught' && d.fielder) return `c ${name(d.fielder)} b ${name(d.bowler)}`;
    if (d.kind === 'caught and bowled') return `c & b ${name(d.bowler)}`;
    if (d.kind === 'stumped') return `st b ${name(d.bowler)}`;
    if (d.kind === 'bowled') return `b ${name(d.bowler)}`;
    if (d.kind === 'lbw') return `lbw b ${name(d.bowler)}`;
    if (d.kind === 'hit wicket') return `hit wicket b ${name(d.bowler)}`;
    return 'obstructing the field';
  }
</script>

<section class="screen active" aria-label="Scorecard">
  <div class="body">
    {#each innings as { name, result } (name)}
      {@const ex = extras(result)}
      <h2 style="margin-top:var(--space-4)">
        {name}
        <span class="muted tabular">
          {result.runs}/{result.wickets} ({oversFrom(result.legalBalls)})
        </span>
      </h2>

      <table class="sc">
        <thead>
          <tr><th>Batter</th><th class="n">R</th><th class="n">B</th><th class="n">4s</th><th class="n">6s</th></tr>
        </thead>
        <tbody>
          {#each result.batting.filter((b) => b.balls > 0 || b.out) as b (b.player.id)}
            <tr>
              <td>{b.player.name}<span class="how">{how(b, result)}</span></td>
              <td class="n">{b.runs}</td>
              <td class="n">{b.balls}</td>
              <td class="n">{b.fours}</td>
              <td class="n">{b.sixes}</td>
            </tr>
          {/each}
        </tbody>
      </table>
      <p class="faint" style="margin-top:var(--space-3)">
        Extras {ex.total} (w {ex.w}, nb {ex.nb}, lb {ex.lb}, b {ex.b})
      </p>

      <p class="eyebrow" style="margin-top:var(--space-4)">Bowling</p>
      <table class="sc">
        <thead>
          <tr><th>Bowler</th><th class="n">O</th><th class="n">M</th><th class="n">R</th><th class="n">W</th></tr>
        </thead>
        <tbody>
          {#each result.bowling.filter((b) => b.balls > 0) as b (b.player.id)}
            <tr>
              <td>{b.player.name}</td>
              <td class="n">{figures(b.balls, 0, 0).split('-')[0]}</td>
              <td class="n">{b.maidens}</td>
              <td class="n">{b.runs}</td>
              <td class="n">{b.wickets}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/each}
  </div>

  <div class="actions"><button class="btn-ghost" onclick={onback}>Back</button></div>
</section>
